import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Database,
  Loader2,
  Plug,
  RefreshCw,
  ShieldCheck,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';

export default function Odoo() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [config, setConfig] = useState({ configured: false });
  const [status, setStatus] = useState({ connected: false });
  const [form, setForm] = useState({
    url: '',
    database: '',
    username: '',
    password: '',
  });

  const refresh = async () => {
    try {
      const [c, s] = await Promise.all([
        api.getOdooCredentials(token),
        api.getOdooStatus(token),
      ]);
      setConfig(c.data);
      setStatus(s.data);
      if (c.data?.configured) {
        setForm((f) => ({
          ...f,
          url: c.data.url || '',
          database: c.data.database || '',
          username: c.data.username || '',
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!form.url.trim() || !form.database.trim() || !form.username.trim() || !form.password.trim()) {
      toast.error('URL, database, username and password are all required');
      return;
    }
    if (!/^https?:\/\//i.test(form.url.trim())) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    setSaving(true);
    try {
      await api.saveOdooCredentials(token, {
        url: form.url.trim(),
        database: form.database.trim(),
        username: form.username.trim(),
        password: form.password.trim(),
      });
      toast.success('Credentials saved. Now click Test connection.');
      setForm((f) => ({ ...f, password: '' }));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await api.testOdooConnection(token);
      toast.success(
        `Connected to ${r.data.company_name || r.data.user_name || 'Odoo'} (v${r.data.server_version || '?'})`
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connection test failed');
      await refresh();
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Odoo? Saved credentials will be removed.')) return;
    setDisconnecting(true);
    try {
      await api.disconnectOdoo(token);
      toast.success('Odoo disconnected');
      setForm({ url: '', database: '', username: '', password: '' });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  const step1Done = !!config.configured;
  const step2Done = status.connected;
  const step3Done = status.connected && !!status.last_verified_at;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="odoo-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
            <Database className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
              Odoo ERP
            </h1>
            <p className="text-slate-400 mt-1">
              Connect your UnTill POS to Odoo — securely and in three steps.
            </p>
          </div>
        </div>
        {status.connected ? (
          <Badge
            className="bg-purple-500/15 text-purple-300 border-purple-500/30 gap-1.5"
            data-testid="odoo-status-badge"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Connected
          </Badge>
        ) : (
          <Badge
            className="bg-slate-500/15 text-slate-400 border-slate-500/30 gap-1.5"
            data-testid="odoo-status-badge"
          >
            <AlertCircle className="w-3.5 h-3.5" /> Not connected
          </Badge>
        )}
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StepBadge n={1} title="Save credentials" done={step1Done} active={!step1Done} />
        <StepBadge n={2} title="Test connection" done={step2Done} active={step1Done && !step2Done} />
        <StepBadge n={3} title="Ready to sync" done={step3Done} active={step2Done && !step3Done} />
      </div>

      {/* Step 1 — credentials */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="odoo-credentials-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-purple-400" />
            Step 1 · Your Odoo credentials
          </CardTitle>
          <CardDescription className="text-slate-400">
            Enter the URL, database name, and an Odoo user + API key with access to Sales, Contacts and Accounting.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Odoo URL</Label>
              <Input
                placeholder="https://mycompany.odoo.com"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm"
                data-testid="odoo-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Database name</Label>
              <Input
                placeholder="mycompany-production"
                value={form.database}
                onChange={(e) => setForm({ ...form, database: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm"
                data-testid="odoo-db-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Username / email</Label>
              <Input
                placeholder="integration@mycompany.com"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-white text-sm"
                data-testid="odoo-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">API key or password</Label>
              <Input
                type="password"
                placeholder={config.configured ? '•••••••• (already saved — paste to change)' : 'Odoo API key'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-white text-sm"
                data-testid="odoo-password-input"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-500 hover:bg-purple-600 text-white"
              data-testid="odoo-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              {config.configured ? 'Update credentials' : 'Save credentials'}
            </Button>
            <a
              href="https://www.odoo.com/documentation/17.0/developer/reference/external_api.html"
              target="_blank"
              rel="noreferrer"
              className="text-sm text-slate-400 hover:text-purple-400 inline-flex items-center gap-1 self-center ml-1"
              data-testid="odoo-docs-link"
            >
              Odoo external API docs <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Step 2 — test */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="odoo-test-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <Plug className="w-5 h-5 text-purple-400" />
            Step 2 · Test connection
          </CardTitle>
          <CardDescription className="text-slate-400">
            We&apos;ll check reachability, verify authentication, and read your Odoo version and contact count.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            onClick={handleTest}
            disabled={!step1Done || testing}
            className="bg-purple-500 hover:bg-purple-600 text-white"
            data-testid="odoo-test-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plug className="w-4 h-4 mr-2" />}
            {status.connected ? 'Re-test connection' : 'Test connection'}
          </Button>
          {!step1Done && (
            <span className="text-xs text-slate-500 self-center">Save your credentials first.</span>
          )}
        </CardContent>
      </Card>

      {/* Step 3 — verified */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="odoo-verify-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-purple-400" />
            Step 3 · Verified &amp; ready
          </CardTitle>
          <CardDescription className="text-slate-400">
            When the check passes, SyncPoint can start posting UnTill data into Odoo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status.connected && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <InfoTile label="Company" value={status.company_name || '—'} />
              <InfoTile label="Odoo user" value={status.user_name || '—'} />
              <InfoTile label="Server version" value={status.server_version || '—'} mono />
              <InfoTile label="Contacts on file" value={String(status.partner_count ?? '—')} />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleDisconnect}
              disabled={!config.configured || disconnecting}
              variant="outline"
              className="border-rose-500/40 text-rose-300 hover:text-white hover:bg-rose-500/20"
              data-testid="odoo-disconnect-btn"
            >
              {disconnecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Disconnect
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help card */}
      <Card className="bg-slate-900/30 border-slate-800/60">
        <CardHeader>
          <CardTitle className="text-base font-heading text-white">Where do I find my Odoo credentials?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2 leading-relaxed">
          <p><span className="text-slate-200 font-medium">1.</span> Log in to your Odoo instance as an admin user.</p>
          <p><span className="text-slate-200 font-medium">2.</span> The <span className="text-slate-200">URL</span> is the address bar host, e.g. <span className="font-mono text-purple-300">https://mycompany.odoo.com</span>.</p>
          <p><span className="text-slate-200 font-medium">3.</span> The <span className="text-slate-200">database name</span> is shown at <span className="font-mono">/web/database/selector</span> (usually the same as your subdomain).</p>
          <p><span className="text-slate-200 font-medium">4.</span> Under <span className="text-slate-200">My Profile → Account Security → New API Key</span>, generate a key labelled &quot;SyncPoint&quot;. Paste it above instead of your password.</p>
          <p><span className="text-slate-200 font-medium">5.</span> Save, then click <span className="text-slate-200">Test connection</span>. That&apos;s it.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function StepBadge({ n, title, done, active }) {
  return (
    <div
      className={
        'rounded-lg border p-4 flex items-center gap-3 ' +
        (done
          ? 'bg-purple-500/10 border-purple-500/30'
          : active
          ? 'bg-slate-900/60 border-purple-500/20'
          : 'bg-slate-900/40 border-slate-800')
      }
      data-testid={`odoo-step-${n}`}
    >
      <div
        className={
          'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ' +
          (done
            ? 'bg-purple-500 text-white'
            : active
            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
            : 'bg-slate-800 text-slate-500')
        }
      >
        {done ? <CheckCircle2 className="w-4 h-4" /> : n}
      </div>
      <div>
        <div className={done ? 'text-purple-200 font-medium' : 'text-white font-medium'}>{title}</div>
        <div className="text-xs text-slate-500">
          {done ? 'Complete' : active ? 'In progress' : 'Pending'}
        </div>
      </div>
    </div>
  );
}

function InfoTile({ label, value, mono }) {
  return (
    <div className="rounded-lg bg-slate-800/40 border border-slate-800 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={'text-sm text-white mt-1 truncate ' + (mono ? 'font-mono' : '')}>{value}</div>
    </div>
  );
}
